<template>
  <header class="header d-flex">
    <!-- title -->
    <div :class="headerClasses" class="header__title">
      <template v-if="hasSubtitle">
        <!-- back -->
        <div
          @click="emit({ name: 'back' })"
          class="mr-2 header__title--back"
          v-if="foldersTree.size < foldersTreeThreshold"
        >
          <v-icon color="neutral-light" size="16px"> mdi-arrow-left</v-icon>
        </div>

        <!-- folders-tree -->
        <base-btn
          v-if="foldersTree.size >= foldersTreeThreshold"
          class="header__title--back"
          type="tertiary"
          icon="mdi-dots-horizontal"
          color="neutral-light"
          @click="emit({ name: 'folders', value: $event })"
        />
      </template>

      <!-- title -->
      <h3
        :class="{ hasSubtitle }"
        :title="title"
        class="header__title--main body-bold-small mr-2"
        @click="emit({ name: 'title' })"
      >
        {{ title }}
      </h3>

      <!-- subtitle -->
      <template v-if="hasSubtitle">
        <div class="header__title--sub divisor d-flex align-center">
          /
          <slot name="subtitle">
            <h4 class="body-regular-small ml-2" :title="subtitle">
              {{ subtitle }}
            </h4>
          </slot>
        </div>
      </template>

      <!-- info -->
      <small v-if="hasInfo" class="header__title--info">
        <slot name="info">
          {{ info }}
        </slot>
      </small>

      <!-- filter -->
      <div v-if="hasFilter" class="header__title--filter">
        <slot name="filter" />
      </div>
    </div>

    <!-- middle -->
    <nav v-if="hasTabs" class="header__nav mx-auto">
      <v-tabs
        v-model="nav.middle"
        class="header__tabs"
        height="4.5rem"
        @change="tab"
      >
        <v-tab
          v-for="({ title, to, href, dataIntro }, index) in menu"
          :key="index"
          :class="activeTab(index, nav.middle)"
          :to="to"
          :href="href"
          :data-intro="dataIntro"
        >
          <span>{{ title }}</span>
        </v-tab>
      </v-tabs>
    </nav>

    <!-- actions -->
    <div v-if="hasActions" class="header__actions" style="margin-left: 0.5rem">
      <!-- actions::slot -->
      <slot name="actions" />
      <!-- intro -->
      <base-intro-reset v-if="hasIntro" v-bind="{ click: refreshIntro }" />
    </div>
  </header>
</template>

<script>
//external imports
import _ from 'lodash'

// internal imports
import introMixin from '@/utils/intro.mixin.js'

export default {
  name: 'BaseHeaderV2Intern',

  mixins: [introMixin],

  components: {
    BaseBtn: () => import('@/components/BaseBtn.vue'),
  },

  props: {
    title: {
      type: String,
      default: '',
      required: false,
    },
    subtitle: {
      type: String,
      default: '',
      required: false,
    },
    info: {
      type: String,
      default: '',
      required: false,
    },
    menu: {
      type: Array,
      default: () => [],
      required: false,
    },
    foldersTree: {
      type: Set,
      default: () => new Set(),
      required: false,
    },
    foldersTreeThreshold: {
      type: Number,
      default: 3,
      required: false,
    },
    hasIntro: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data: () => ({
    hasFilter: false,
    hasActions: false,
    hasSlotSubtitle: false,
    hasSlotInfo: false,
    nav: {
      middle: 0,
    },
  }),

  computed: {
    hasTabs() {
      return !!this.menu.length
    },

    hasSubtitle() {
      return this.hasSlotSubtitle || !!this.subtitle
    },

    hasInfo() {
      return this.hasSlotInfo || !!this.info
    },

    headerClasses() {
      const options = [
        this.hasSubtitle ? 'a' : '',
        this.hasInfo ? 'b' : '',
        this.hasFilter ? 'c' : '',
      ]

      return options.join('').concat(this.info ? ' hasInfo' : '')
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.$nextTick(() => {
        this.updateConditions()
      })
    })
  },

  methods: {
    emit({ name, value = true }) {
      if (this.$router?.history?.current?.fullPath.includes('tutor')) {
        this.$router.push({ name: 'Tutor' })
        return
      }
      this.$emit(name, value)
    },

    tab(value) {
      this.emit({ name: 'tab', value })
    },

    updateConditions() {
      this.hasFilter = _.has(this.$slots, 'filter')
      this.hasActions = _.has(this.$slots, 'actions')
      this.hasSlotSubtitle = _.has(this.$slots, 'subtitle')
      this.hasSlotInfo = _.has(this.$slots, 'info')
    },

    activeTab(index, prop, defaultTabIndex = 0) {
      const isGivenTab = index === defaultTabIndex
      const differsFromCurrentPath = prop !== this.$route.path

      return {
        'v-tab--active': isGivenTab && differsFromCurrentPath,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_tabs';

.header {
  min-height: 4.5rem;
  padding: 0 $spacing-plus-1;
  background-color: var(--v-neutral-lighten4);

  align-items: center;
  justify-content: space-around;

  @media (min-width: 680px) {
    border-bottom: 1px solid var(--v-neutral-lighten3);
    justify-content: space-between;
  }

  &__title {
    display: none;

    grid-template-areas: 'main';
    grid-template-columns: auto;
    grid-template-rows: auto;

    align-items: center;

    @media (min-width: 680px) {
      margin-right: 10rem;
      display: grid;
    }

    &.hasInfo {
      grid-template-rows: repeat(2, auto);
    }

    /**
     * a: subtitle
     * b: info
     * c: filter
     */

    // subtitle only
    &.a {
      grid-template-areas: 'back main sub';
      grid-template-columns: auto auto 1fr;
    }

    // info only
    &.b {
      grid-template-areas: 'main' 'info';
      grid-template-columns: auto;
    }

    // filter only
    &.c {
      grid-template-areas: 'main filter';
      grid-template-columns: 1fr auto;
    }

    // subtitle && info
    &.ab {
      grid-template-areas: 'back main sub' 'back info info';
      grid-template-columns: auto auto 1fr;
    }

    // subtitle && filter
    &.ac {
      grid-template-areas: 'back main sub filter';
      grid-template-columns: auto auto 1fr auto;
    }

    // info && filter
    &.bc {
      grid-template-areas: 'main filter' 'info filter';
      grid-template-columns: 1fr auto;
    }

    // all
    &.abc {
      grid-template-areas:
        'back main sub filter'
        'back info info filter';
      grid-template-columns: auto auto 1fr auto;
    }

    &--main,
    &--sub > ::v-deep *:not(.v-icon) {
      line-height: 120%;
      color: varFigma(neutral-20-dark);
    }

    &--back {
      grid-area: back;
      margin-right: $spacing-plus-1;
      cursor: pointer;

      .v-icon {
        color: varFigma(neutral-40-light);
      }
    }

    &--main {
      grid-area: main;

      &.hasSubtitle {
        cursor: pointer;
      }
    }

    &--sub {
      grid-area: sub;
      overflow-x: hidden;
      overflow-y: hidden;
      color: varFigma(neutral-40-light);

      .v-icon {
        margin: 0 $spacing-minus-2;
      }
    }

    &--info {
      grid-area: info;

      margin-top: $spacing-minus-4;
      color: var(--v-neutral-lighten1);
    }

    &--filter {
      grid-area: filter;
      margin-left: $spacing-plus-1;
    }
  }

  &__nav {
    display: none;

    @media (min-width: 680px) {
      display: block;
    }
  }

  &__tabs {
    @include v-tabs();
  }

  &__actions {
    display: flex;
    align-items: center;

    &::v-deep {
      > *:not(:first-child) {
        margin-left: $spacing-base;
      }
    }
  }
}

.bg-dark {
  .header {
    background-color: var(--v-neutral-darken2);
    border-color: var(--v-neutral-darken1);

    &__title {
      &--main,
      &--sub h4,
      &--sub > div {
        color: var(--v-brand-lighten2);
      }

      &--back {
        .v-icon {
          color: var(--v-brand-lighten1);
        }
      }

      &--sub {
        grid-area: sub;

        .v-icon {
          color: var(--v-brand-lighten2);
        }
      }

      &--info {
        color: var(--v-brand-lighten1);
      }
    }
  }

  .divisor {
    color: varFigma(neutral-80-lighter) !important;
  }
}
</style>
